/**
 * Libellés des statuts d'une quête, de son paiement et de ses soumissions.
 *
 * Une seule table pour la liste, le détail et les cycles : chaque page avait
 * la sienne et elles divergeaient (« brouillon » affiché brut ici, tout statut
 * inconnu devenu « Brouillon » là). Un statut inconnu s'affiche tel quel
 * plutôt que de se faire passer pour un autre.
 *
 * Le « ton » laisse chaque page appliquer ses propres couleurs.
 */
export type TonStatut = "succes" | "attente" | "erreur" | "accent" | "neutre";

export interface StatutAffiche { libelle: string; ton: TonStatut; }

/* Valeurs écrites par location-service. */
const STATUTS_QUETE: Record<string, StatutAffiche> = {
  brouillon:             { libelle: "Brouillon", ton: "accent" },
  en_attente_validation: { libelle: "À valider", ton: "attente" },
  publiee:               { libelle: "Publiée",   ton: "succes" },
  archivee:              { libelle: "Archivée",  ton: "neutre" },
};

export function statutQuete(statut?: string | null): StatutAffiche {
  const cle = (statut ?? "").toLowerCase();
  return STATUTS_QUETE[cle] ?? { libelle: statut || "—", ton: "neutre" };
}

/* Étapes saisies avant facturation (devis_envoye → devis_signe), puis valeurs
   dérivées par le serveur des factures et des encaissements (lot 2) :
   facture, partiellement_paye, paye, trop_percu. Plus aucune ne se pose à la
   main après le devis. */
const STATUTS_PAIEMENT: Record<string, StatutAffiche> = {
  devis_envoye:       { libelle: "Devis envoyé",        ton: "attente" },
  devis_signe:        { libelle: "Devis signé",         ton: "accent" },
  facture:            { libelle: "Facturée",            ton: "attente" },
  partiellement_paye: { libelle: "Partiellement payée", ton: "accent" },
  paye:               { libelle: "Payée",               ton: "succes" },
  trop_percu:         { libelle: "Trop-perçu",          ton: "erreur" },
};

/** `siAucun` : libellé quand aucun statut de paiement n'est encore posé. */
export function statutPaiement(statut: string | null | undefined, siAucun: string): StatutAffiche {
  if (!statut) return { libelle: siAucun, ton: "neutre" };
  return STATUTS_PAIEMENT[statut] ?? { libelle: statut, ton: "neutre" };
}

/* « expire » est posé par le job d'expiration sur une soumission jamais traitée. */
const STATUTS_SOUMISSION: Record<string, StatutAffiche> = {
  en_attente: { libelle: "En attente", ton: "attente" },
  valide:     { libelle: "Validée",    ton: "succes" },
  rejete:     { libelle: "Rejetée",    ton: "erreur" },
  expire:     { libelle: "Expirée",    ton: "neutre" },
};

export function statutSoumission(statut?: string | null): StatutAffiche {
  const cle = (statut || "en_attente").toLowerCase();
  return STATUTS_SOUMISSION[cle] ?? { libelle: statut || "—", ton: "neutre" };
}
